import { AfterViewInit, ChangeDetectionStrategy, Component, EventEmitter, forwardRef, Input, OnDestroy, OnInit, Output, QueryList, Signal, ViewChildren, computed, signal } from '@angular/core';
import { Subscription } from 'rxjs';
import { EInvoiceViewerSearchHandler } from '../../interfaces/einvoice-viewer-search-handler.interface';
import { EInvoiceViewerSearchOptions } from '../../interfaces/einvoice-viewer-search-options.interface';
import { SchemaField } from '../../interfaces/schemafields.interface';
import { Attachment } from '../../services/einvoice-viewer.service';
import { CodelistTranslationService } from '../../services/codelist-translation.service';
import { EinvoiceViewerContextService } from '../../services/einvoice-viewer-context.service';
import { EinvoiceViewerSearchService } from '../../services/einvoice-viewer-search.service';
import { createSelectionFromNode } from '../../utils/provenance-index';
import { matchesSearchTexts, normalizeSearchText } from '../../utils/viewer-search.util';
import { FieldComponent } from './field/field.component';
import { DocumentSearchEntry, EINVOICE_DOCUMENT_SEARCH_STATE, EinvoiceDocumentSearchState } from './document-search-state';

@Component({
    selector: 'app-einvoice-document',
    templateUrl: './einvoice-document.component.html',
    styleUrls: ['./einvoice-document.component.scss'],
    providers: [{ provide: EINVOICE_DOCUMENT_SEARCH_STATE, useExisting: forwardRef(() => EinvoiceDocumentComponent) }],
  changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class EinvoiceDocumentComponent implements OnInit, AfterViewInit, OnDestroy, EInvoiceViewerSearchHandler, EinvoiceDocumentSearchState {
  @Input() document: any | undefined;
  @Input() additionalAttachments: Attachment[] | undefined;
  @Input() filename: string | undefined;
  @Input() locale: string | undefined;
  @Output() onError = new EventEmitter<{ message: string, title?: string }>();
  @ViewChildren(FieldComponent) private readonly fieldComponents?: QueryList<FieldComponent>;

  fields: SchemaField[] | undefined;
  attachments: any[] | undefined;
  readonly searchOptions = signal<EInvoiceViewerSearchOptions>({ query: '', exactMatch: false });
  readonly activeSearchResultIndex = signal(0);
  private readonly fieldsRevision = signal(0);
  private fieldComponentsSubscription: Subscription | undefined;
  readonly searchEntries = computed(() => {
    this.fieldsRevision();
    return this.fieldComponents?.toArray()
      .map(field => field.getSearchEntry())
      .filter((entry): entry is DocumentSearchEntry => entry !== undefined) ?? [];
  });
  readonly searchResults = computed(() => {
    const searchOptions = this.searchOptions();
    if (!normalizeSearchText(searchOptions.query)) {
      return [];
    }

    return this.searchEntries()
      .filter(entry => matchesSearchTexts(entry.searchTexts, searchOptions))
      .map(entry => entry.id);
  });
  readonly searchResultIds = computed<ReadonlySet<string>>(() => new Set(this.searchResults()));
  readonly matchCount = computed(() => this.searchResults().length);
  readonly activeMatchIndex = computed(() => {
    const matchCount = this.searchResults().length;
    if (matchCount === 0) {
      return -1;
    }

    return Math.min(this.activeSearchResultIndex(), matchCount - 1);
  });
  readonly currentSearchResultId = computed(() => {
    const activeMatchIndex = this.activeMatchIndex();
    return activeMatchIndex === -1 ? undefined : this.searchResults()[activeMatchIndex];
  });

  get dateFormat(): string {
    const locale = this.locale;
    return locale === 'de' ? 'dd.MM.yyyy' : 'yyyy-MM-dd';
  }

  numberFormat = "1.2-2";

  constructor(
    private codelistTranslationService: CodelistTranslationService,
    private einvoiceViewerSearchService: EinvoiceViewerSearchService,
    private readonly einvoiceViewerContextService: EinvoiceViewerContextService,
  ) {
    this.einvoiceViewerSearchService.registerHandler('document', this);
  }

  async ngOnInit(): Promise<void> {
    this.attachments = this.document["AdditionalSupportingDocuments"];
  }

  ngAfterViewInit(): void {
    this.fieldsRevision.update(revision => revision + 1);
    this.fieldComponentsSubscription = this.fieldComponents?.changes.subscribe(() => {
      this.fieldsRevision.update(revision => revision + 1);
    });
  }

  ngOnDestroy(): void {
    this.fieldComponentsSubscription?.unsubscribe();
    this.einvoiceViewerSearchService.unregisterHandler('document', this);
  }

  setSearchQuery(options: EInvoiceViewerSearchOptions): void {
    this.searchOptions.set(options);
    this.activeSearchResultIndex.set(0);
  }

  nextSearchResult(): void {
    this.moveSearchResult(1);
  }

  previousSearchResult(): void {
    this.moveSearchResult(-1);
  }

  scrollToActiveResult(): void {
    const currentSearchResultId = this.currentSearchResultId();
    if (!currentSearchResultId) {
      return;
    }

    const field = this.fieldComponents?.toArray().find(candidate => candidate.searchFieldId === currentSearchResultId);
    requestAnimationFrame(() => field?.scrollIntoView());
  }

  getEmailSubject(): string {
    return $localize`:@@einvoice.header.fields.email-subject-prefix:e-Invoice no. `
      + `${this.document["InvoiceNumber"].value} ${this.document["InvoiceTypeCode"].value} (${this.filename})`;
  }
  hasValue(documentPart: any): boolean {
    return documentPart !== undefined && documentPart !== null;
  }

  get invoiceLines(): any[] {
    return this.document["InvoiceLines"] as [];
  }

  lineIdentifier(invoiceLine: any, index: number): string {
    const identifier = invoiceLine?.InvoiceLineIdentifier?.value?.toString().trim();
    if (!identifier) {
      return (index + 1).toString().padStart(3, '0');
    }
    return /^\d+$/.test(identifier) ? identifier.padStart(3, '0') : identifier;
  }

  lineHasDiscountPerPrice(invoiceLine: any): boolean {
    return invoiceLine !== undefined && invoiceLine !== null &&
      invoiceLine.PriceDetails &&
      invoiceLine.PriceDetails.ItemPriceDiscount && invoiceLine.PriceDetails.ItemPriceDiscount.value !== 0;
  }
  showHeaderDeliveryAddress(): boolean {
    return this.document.DeliveryInformation && (
      this.document.DeliveryInformation.DeliverToPartyName ||
      this.document.DeliveryInformation.DeliverToAddress?.AddressLine1 ||
      this.document.DeliveryInformation.DeliverToAddress?.AddressLine2 ||
      this.document.DeliveryInformation.DeliverToAddress?.AddressLine3 ||
      this.document.DeliveryInformation.DeliverToAddress?.PostCode ||
      this.document.DeliveryInformation.DeliverToAddress?.City ||
      this.document.DeliveryInformation.DeliverToAddress?.CountrySubdivision ||
      this.document.DeliveryInformation.DeliverToAddress?.CountryCode ||
      this.document.DeliveryInformation.DeliverToLocationIdentifier
    );
  }

  private async getAttachmentData(attachment: any): Promise< { blob: Blob, filename: string } | undefined> {
    try {
      if (attachment.AttachedDocument && attachment.AttachedDocument.Content) {
        return {
          blob: await this.base64ToBlob(attachment.AttachedDocument.Content, attachment.AttachedDocument.MimeCode),
          filename: attachment.AttachedDocument.Filename
        }
      }
      const uri = attachment.ExternalDocumentLocation?.value;
      if (uri) {
        if (uri.startsWith("#ef=")) {
          // ZUGFeRD reference
          const ef = uri.substring(4);
          const fromPDF = this.additionalAttachments?.find(a => a.filename === ef);
          if (fromPDF) {
            return {
              blob: new Blob([fromPDF.content]),
              filename: fromPDF.filename
            };
          }
        }
      }
      return undefined;
    } catch (e) {
      console.error(e);
      return undefined;
    }
  }

  get erechnungPrefix(): string {
    return this.codelistTranslationService.erechnungPrefix;
  }

  async openAttachment(attachment: any) {
    const blob = await this.getAttachmentData(attachment);
    if (blob) {
      const url = URL.createObjectURL(blob.blob);
      const attachmentWindow = window.open(url, '_blank');
      if (attachmentWindow) {
        attachmentWindow.onload = function () {
          setTimeout(() => {
            attachmentWindow.document.title = blob.filename;
          }, 1000);
        };
      }
    }
  }

  async downloadAttachment(attachment: any) {
    const blob = await this.getAttachmentData(attachment);
    if (blob) {
      const url = URL.createObjectURL(blob.blob);

      const link = document.createElement("a");
      link.style.display = "none";
      link.href = url;
      link.download = blob.filename;
      link.click();
      link.remove();
    }
    else {
      this.onError.emit({
        message: $localize`:@@einvoice.error.attachment-missing-data:Attachment is missing data and cannot be downloaded.`,
        title: $localize`:@@einvoice.error.unexpected-error:Unexpected error`
      });
    }
  }

  async selectAndDownloadAttachment(event: MouseEvent, attachment: any): Promise<void> {
    event.preventDefault();

    const selectionEntry = this.getAttachmentSelectionEntry(attachment);

    if (selectionEntry) {
      this.einvoiceViewerContextService.select(selectionEntry, event.ctrlKey || event.metaKey);
    }

    await this.downloadAttachment(attachment);
  }

  isAttachmentSelected(attachment: any): boolean {
    return this.einvoiceViewerContextService.isSelected(this.getAttachmentSelectionEntry(attachment)?.modelPath);
  }

  isAttachmentActive(attachment: any): boolean {
    return this.einvoiceViewerContextService.isActive(this.getAttachmentSelectionEntry(attachment)?.modelPath);
  }

  private getAttachmentSelectionEntry(attachment: any) {
    return createSelectionFromNode(attachment?.SupportingDocumentReference)
      ?? createSelectionFromNode(attachment?.SupportingDocumentDescription)
      ?? createSelectionFromNode(attachment?.AttachedDocument);
  }

  updateDocument(document: any) {
    this.document = document;
    this.attachments = this.document["AdditionalSupportingDocuments"];
  }

  discountsForAllLines(): any {
    const discounts = this.document["InvoiceLines"].map((line: any) => {
      return line.InvoiceLineAllowances;
    });
    return discounts;
  }
  invoiceLineAllowances(invoiceLine: any): any[] {
    return invoiceLine.InvoiceLineAllowances.filter(
      (x: any) => !this.discountLineHidden(x)
    );
  }

  invoiceLineCharges(invoiceLine: any): any[] {
    return invoiceLine.InvoiceLineCharges.filter(
      (x: any) => !this.expenseLineHidden(x)
    );
  }
  expenseLineHidden(expenseLine: any): boolean {
    return (!expenseLine?.InvoiceLineChargePercentage?.value || expenseLine?.InvoiceLineChargePercentage?.value == 0) &&
      (!expenseLine?.InvoiceLineChargeAmount?.value || expenseLine?.InvoiceLineChargeAmount?.value == 0);
  }
  discountLineHidden(discountLine: any): boolean {
    return (!discountLine?.InvoiceLineAllowancePercentage?.value || discountLine?.InvoiceLineAllowancePercentage?.value == 0) &&
      (!discountLine?.InvoiceLineAllowanceAmount?.value || discountLine?.InvoiceLineAllowanceAmount?.value == 0);
  }
  vatBreakdownHidden(vatBreakdown: any): boolean {
    return (!vatBreakdown?.VATCategoryTaxableAmount?.value || vatBreakdown?.VATCategoryTaxableAmount?.value == 0) &&
      (!vatBreakdown?.VATCategoryTaxAmount?.value || vatBreakdown?.VATCategoryTaxAmount?.value == 0);
  }
  discountsAndExpensesHaveValue(): boolean {
    return this.documentLevelCharges.length > 0 || this.documentLevelAllowances.length > 0;
  }
  chargeRowValueAreZero(chargeRow: any): boolean {
    return (!chargeRow.DocumentLevelChargePercentage?.value || chargeRow.DocumentLevelChargePercentage?.value === 0) &&
      (!chargeRow.DocumentLevelChargeBaseAmount?.value || chargeRow.DocumentLevelChargeBaseAmount?.value === 0) &&
      (!chargeRow.DocumentLevelChargeAmount?.value || chargeRow.DocumentLevelChargeAmount?.value === 0);
  }
  allowanceRowValueAreZero(chargeRow: any): boolean {
    return (!chargeRow.DocumentLevelAllowancePercentage?.value || chargeRow.DocumentLevelAllowancePercentage?.value === 0) &&
      (!chargeRow.DocumentLevelAllowanceBaseAmount?.value || chargeRow.DocumentLevelAllowanceBaseAmount?.value === 0) &&
      (!chargeRow.DocumentLevelAllowanceAmount?.value || chargeRow.DocumentLevelAllowanceAmount?.value === 0);
  }
  get sellerIdentifierBT29(): any {
    return this.document?.Seller?.SellerIdentifier[0] || '';
  }
  get documentLevelCharges(): any[] {
    return this.document?.DocumentLevelCharges.filter((x: any) => !this.chargeRowValueAreZero(x));
  }
  get documentLevelAllowances(): any[] {
    return this.document?.DocumentLevelAllowances.filter((x: any) => !this.allowanceRowValueAreZero(x));
  }
  get documentLevelAllowancePercentagesAreEmpty(): boolean {
    return this.document?.DocumentLevelAllowances.every((x: any) => !x.DocumentLevelAllowancePercentage?.value || x.DocumentLevelAllowancePercentage?.value === 0);
  }
  get documentLevelAllowanceBaseAmountsAreEmpty(): boolean {
    return this.document?.DocumentLevelAllowances.every((x: any) => !x.DocumentLevelAllowanceBaseAmount?.value || x.DocumentLevelAllowanceBaseAmount?.value === 0);
  }
  getTranslation(key: string): string {
    switch (key) {
      case "einvoice.footer.description.uid": return $localize`:@@einvoice.footer.description.uid:UID: `;
      case "einvoice.footer.description.taxid": return $localize`:@@einvoice.footer.description.taxid:Tax ID: `;
      case "einvoice.table.description-line.expense-in-percent-per-abs": return $localize`:@@einvoice.table.description-line.expense-in-percent-per-abs:Expense (Base Amount ${this.document.InvoiceCurrencyCode?.value}:Currency: / in % / abs.) `;
      case "einvoice.table.description-line.discount-in-percent-per-abs": return $localize`:@@einvoice.table.description-line.discount-in-percent-per-abs:Discount (Base Amount ${this.document.InvoiceCurrencyCode?.value}:Currency: / in % / abs.) `;
      default: return "";
    }
  }
  async base64ToBlob(base64: string, mimeCode: string): Promise<Blob> {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: mimeCode });
  }

  private moveSearchResult(direction: 1 | -1): void {
    const searchResults = this.searchResults();
    if (searchResults.length === 0) {
      return;
    }

    const nextIndex = (this.activeSearchResultIndex() + direction + searchResults.length) % searchResults.length;
    this.activeSearchResultIndex.set(nextIndex);
    this.scrollToActiveResult();
  }
}
